import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ApolloError } from '@apollo/client/errors';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';

import { ErrorPolicy, FetchPolicy, UserContext } from '@hdms/shared/common-types';
import {
  CookieService,
  EnvironmentService,
  FeatureToggleService,
  LogoutService,
  PlatformService,
  StorageService,
  StorageType,
  b64DecodeUnicode,
  b64EncodeUnicode
} from '@hdms/shared/util-core';

import { ACCOUNT_COOKIE_NAME, SHIPTO_ID_COOKIE_NAME, SSO_USER_ROLE_COOKIE_NAME } from '../constants';
import { getLocalization } from '../helper';
import { FT_EVOUCHERS, FT_GLOBAL_AUTHORIZATION_MANAGEMENT, FT_HDMS_CCMSV2_FLEET_ACCOUNT } from '../toggles';

import { LoggedInContextGqlService } from './logged-in-context-gql.service';
import { SelectableAccount, UserContextInput, UserContextService } from './user-context.service';

interface AccountCookieData {
  // refers to `Customer.originId`
  id: string;
  // refers to `Customer.address.company` name
  c: string;
  // refers to `Customer.address.company2` name
  c2: string;
  // refers to `Contact.formatted` (first + last name)
  n: string;
}

interface UserDetailsAccountData {
  // refers to `Customer.originId`
  accountNo: string;
  // refers to `Customer.address.company` name
  companyName: string;
}

interface UserDetailsResponse {
  accounts: UserDetailsAccountData[];
}

const USER_DETAILS_ENDPOINT_URL = '/userDetails.json?addAccountDetails=true';

function mapUserDetailsToAccountData(data: UserDetailsAccountData[]): AccountCookieData[] {
  return data.map(
    ({ accountNo, companyName }) =>
      ({
        id: accountNo,
        c: companyName
      } as AccountCookieData)
  );
}

function mapAccountDataToSelectableAccounts(data: AccountCookieData[]): SelectableAccount[] {
  return data.map(
    ({ id, c, c2, n }) =>
      ({
        account: {
          originId: id,
          address: {
            company: c,
            formatted: [c, c2].filter(Boolean)
          }
        },
        contact: {
          formatted: [n]
        }
      } as SelectableAccount)
  );
}

function decodeAccountData(encoded: string): AccountCookieData[] {
  try {
    return JSON.parse(b64DecodeUnicode(encoded));
  } catch (e) {
    return [];
  }
}

function parseEncodedAccountData(encoded: string): SelectableAccount[] {
  return mapAccountDataToSelectableAccounts(decodeAccountData(encoded));
}

@Injectable({
  providedIn: 'root'
})
export class UserContextLoaderService {
  private initialized = false;

  constructor(
    private readonly loggedInContextGql: LoggedInContextGqlService,
    private readonly contextService: UserContextService,
    private readonly storageService: StorageService,
    private readonly logoutService: LogoutService,
    private readonly cookieService: CookieService,
    private readonly httpClient: HttpClient,
    private readonly platform: PlatformService,
    private readonly env: EnvironmentService,
    private readonly ftService: FeatureToggleService
  ) {}

  public init(): void {
    if (!this.initialized) {
      this.cleanupOnLogout();
      this.initializeContext();
      this.initialized = true;
    }
  }

  /**
   * Allows manual context (re)-fetching, which also sets the data in UserContextService
   */
  public fetchContext$(): Observable<UserContextInput> {
    return this.getContext$().pipe(tap(data => this.contextService.setData(data)));
  }

  /**
   * Returns observable to
   * - load loggedInContext
   * - get accounts from cookie (if present)
   * - set base tracking-data
   *
   * On server side don't store response in Apollo cache if page is going to be cached
   */
  private getContext$(): Observable<UserContextInput> {
    const isFleetPropEnabled = this.ftService.getToggleById(FT_HDMS_CCMSV2_FLEET_ACCOUNT);
    return this.loggedInContextGql
      .fetch(
        {
          localization: getLocalization(this.env),
          eVouchersEnabled: this.ftService.getToggleById(FT_EVOUCHERS),
          isFleetPropEnabled,
          legacyFleetEnabled:
            !isFleetPropEnabled && !this.env.isByDesign && !this.contextService.isEVoucherBackofficeUser,
          isGlobalAuthorizationManagementEnabled: this.ftService.getToggleById(FT_GLOBAL_AUTHORIZATION_MANAGEMENT)
        },
        {
          errorPolicy: ErrorPolicy.IGNORE,
          fetchPolicy: FetchPolicy.NETWORK_ONLY
        }
      )
      .pipe(
        map(({ data }) => data.loggedInContext),
        switchMap<UserContext, Observable<[UserContext, SelectableAccount[]]>>(data =>
          this.getUserAccounts$(data).pipe(map(accounts => [data, accounts]))
        ),
        map(([data, accounts]) => ({
          data,
          accounts,
          error: !data ? new ApolloError({}) : null
        })),
        catchError((error: ApolloError) => of<UserContextInput>({ error }))
      );
  }

  /**
   * Apply context observable to UserContextService
   * @private
   */
  private initializeContext(): void {
    this.contextService.setContext$(this.platform.isBrowser ? this.getContext$() : of({}));
  }

  private getUserAccounts$(ctx: UserContext): Observable<SelectableAccount[]> {
    return this.contextService.isCustomer && ctx ? this.retrieveUserAccountsData$() : of([]);
  }

  private cleanupOnLogout(): void {
    // clean-up browser storage and cookies on logout
    this.logoutService.logout$().subscribe(() => {
      this.storageService.removeItem(ACCOUNT_COOKIE_NAME);
      this.storageService.removeItem(ACCOUNT_COOKIE_NAME, StorageType.SESSION);
      this.cookieService.delete(SHIPTO_ID_COOKIE_NAME);
      this.cookieService.delete(SSO_USER_ROLE_COOKIE_NAME);
    });
  }

  /**
   * Preliminary solution to retrieve available accounts via account cookie set by oauth-client
   * in standalone mode, or from userDetails endpoint when running in Hybris/AEM host context.
   * The account data will be provided as base64 encoded cookie value containing a json string.
   * After retrieving cookie contents, the value will be transferred to browser storage.
   * In standalone context it'll be stored in local storage, otherwise in session storage.
   * Reason: in standalone context it's not possible to re-request the value after cookie
   * has been removed, so it has to be persisted in local storage so it'll still be
   * available e.g. when duplicating browser tab etc., whereas in Hybris/AEM host context,
   * the value can always be re-requested from userDetails endpoint.
   * TODO: the implementation will be refactored to use graphQL data once HC is ready.
   * @private
   */
  private retrieveUserAccountsData$(): Observable<SelectableAccount[]> {
    const encodedData = this.getEncodedAccountsData();

    return encodedData
      ? of(parseEncodedAccountData(encodedData))
      : this.httpClient.get<UserDetailsResponse>(USER_DETAILS_ENDPOINT_URL).pipe(
          catchError(_ => of({} as UserDetailsResponse)),
          map(({ accounts }) => mapUserDetailsToAccountData(accounts || [])),
          tap(data =>
            this.storageService.setItem(
              ACCOUNT_COOKIE_NAME,
              b64EncodeUnicode(JSON.stringify(data)),
              StorageType.SESSION
            )
          ),
          map(data => mapAccountDataToSelectableAccounts(data))
        );
  }

  private getEncodedAccountsData(): string {
    const cookieAccounts = this.cookieService.get<string>(ACCOUNT_COOKIE_NAME);

    // if there's a cookie, transfer its value to session storage and delete it.
    // check typeof string as cookie value can be empty for resetting browser storage value
    if (typeof cookieAccounts === 'string') {
      this.storageService.setItem(ACCOUNT_COOKIE_NAME, cookieAccounts);
      this.cookieService.delete(ACCOUNT_COOKIE_NAME);
    }

    return (
      cookieAccounts ||
      this.storageService.getItem<string>(ACCOUNT_COOKIE_NAME) ||
      this.storageService.getItem<string>(ACCOUNT_COOKIE_NAME, StorageType.SESSION)
    );
  }
}
